import { inject, Injectable } from '@angular/core';
import { EventDao } from '../db-models/event-dao';
import { EventDateDao } from '../db-models/event-date-dao';
import { CurrentEvent, EventCartItem } from '../models/cart.model';
import { EventState } from '../models/state.model';
import { HelperService } from './helper.service';
import { LocalStorageService } from './local-storage.service';
import { UtilService } from './util.service';
import { isVatAddedOnTop, TaxableLine } from './vat-calculation.util';
import { WidgetUtilService } from './widget-util.service';

@Injectable({
  providedIn: 'root',
})
export class EventCartService {

  private widgetUtilService = inject(WidgetUtilService);
  private localStorageService = inject(LocalStorageService);
  private utilService = inject(UtilService);
  private helperService = inject(HelperService);

  totalCartDuration = 0;
  totalCartPrice = 0;
  addonsCustomFieldsPrice = 0;
  /** Taxable lines collected from every cart item — participants factor applied by the caller. */
  totalCartTaxableLines: TaxableLine[] = [];

  addToCart(
    eventInfo: EventDateDao,
    eventState: EventState,
    cart: EventCartItem[],
    cartLsName: string,
    events: EventDao[],
    overwrite: boolean,
    callback: any
  ): void {
    const cartItem = this.createEventCartItem(eventState, eventInfo, events);
    this.localStorageService.getItem(cartLsName, (lsCart: EventCartItem[]) => {
      cart = this.initCartValue(lsCart, cart);

      if (overwrite) {
        cart.push(cartItem);
      } else {
        cart = [];
        cart.push(cartItem);
      }

      this.utilService.updateLocalStorage(cartLsName, cart);
      this.recalculateCart(cart);
      callback(cart);
    });
  }

  removeFromCart(
    cartItemId: number,
    cartLsName: string,
    cart: EventCartItem[],
    callback: any
  ): void {
    this.localStorageService.getItem(cartLsName, (lsCart: EventCartItem[]) => {
      cart = this.initCartValue(lsCart, cart);
      const deletedCartObject = this.deleteCartItemFromCart(cartItemId, cart);
      cart = deletedCartObject.cart;
      this.utilService.updateLocalStorage(cartLsName, cart);
      this.recalculateCart(cart);
      callback(cart, deletedCartObject.deletedItem);
    });
  }

  recalculateCart(cart: EventCartItem[]): void {
    if (!cart || cart.length === 0) return;

    // Reset totals
    this.totalCartDuration = 0;
    this.totalCartPrice = 0;
    this.totalCartTaxableLines = [];

    const vatOnTop = isVatAddedOnTop(this.widgetUtilService.widgetConf?.widget_vat_type);

    for (const item of cart) {
      // Accumulate total duration
      this.totalCartDuration += item.duration;

      const price = item.currentEvent?.price || 0;
      const rate = item.currentEvent?.taxRate || 0;

      // totalCartPrice stays NET — VAT is computed and added on top by the caller (after the
      // coupon), never folded in here.
      if (price > 0) {
        this.totalCartPrice += price;
      }

      if (vatOnTop && price > 0 && rate > 0) {
        this.totalCartTaxableLines.push({ netCents: Math.round(price * 100), rateHundredths: Math.round(rate * 100) });
      }
    }
    this.totalCartPrice += this.addonsCustomFieldsPrice;
  }

  initCartValue(
    lsCart: EventCartItem[],
    cart: EventCartItem[]
  ): EventCartItem[] {
    cart = (lsCart != null) ? lsCart : [];
    return cart;
  }

  deleteCartItemFromCart(
    cartItemId: number,
    cart: EventCartItem[]
  ): Record<string, EventCartItem[]> {
    let deletedItem = null;
    for (let i = cart.length - 1; i >= 0; i--) {
      if (cart[i].slotId === cartItemId) {
        deletedItem = cart[i];
        cart.splice(i, 1);
      }
    }
    return { cart, deletedItem };
  }

  createEventCartItem(
    eventState: EventState,
    eventInfo: EventDateDao,
    events: EventDao[]
  ): EventCartItem {
    const cartItem = {
      slotId: eventInfo?.id,
      workerId: eventInfo?.worker_id,
      date: eventState.date,
      startTime: eventInfo?.start,
      endTime: eventInfo?.end,
      duration: eventInfo?.duration,
      eventId: eventState.eventId,
      currentEvent: this.getCurrentEvent(eventState.eventId, events),
      category: eventInfo?.event_category || null,
      is_waitinglist_enabled: !!Number(eventInfo.is_waitinglist_enabled),
      is_waitinglist: eventInfo.isWaitingList,
      regular_booking_count: eventInfo.regular_booking_count,
      regular_capacity: eventInfo.regular_capacity,
      waitinglist_booking_count: eventInfo.waitinglist_booking_count,
      waitinglist_capacity: eventInfo.waitinglist_capacity,
      available_regular_capacity: eventInfo.regular_capacity - eventInfo.regular_booking_count,
      available_waitinglist_capacity: eventInfo.waitinglist_capacity - eventInfo.waitinglist_booking_count,
      location: eventInfo?.location || null,
      meeting_type_id: eventInfo?.meeting_type_id
    } as EventCartItem;

    if (events?.length) {
      const foundedEvent = events?.find(event => (event.uuid == eventState.eventId || event.id == eventState.eventId));
      foundedEvent && (cartItem.referenceEvent = foundedEvent);
    }

    // If `has_one_slot` is enabled for mulitday event then find child slots as needs to be shown
    // in summary page
    cartItem.childSlots = [];
    if (eventInfo?.parent_id && eventState?.has_one_slot === 1 && eventState?.slots?.length) {
      cartItem.childSlots = eventState.slots.filter(slot => slot.parent_id === eventInfo.parent_id);
      cartItem.childSlots.sort(this.helperService.sortArrayObjectsByField('date'));
    } else if (!eventInfo?.parent_id && eventInfo?.child_slots?.length) {
      cartItem.childSlots = [...eventInfo.child_slots].sort(this.helperService.sortArrayObjectsByField('date'));
    }

    return cartItem;
  }

  getCurrentEvent(eventId: string | number, events: EventDao[]): CurrentEvent {
    for (const i in events) {
      if (events[i]['uuid'] == eventId || events[i]['id'] == eventId) {
        const tax = events[i]['tax'] ? events[i]['tax'].rate : 0;
        const taxPrice = events[i]['price'] > 0 ? Number(((events[i]['price'] / 100) * tax).toFixed(2)) : 0;

        return {
          title: events[i]['title'],
          description: events[i]['description'],
          place: events[i]['place'],
          image: events[i]['image'],
          price: events[i]['price'],
          tax: taxPrice,
          taxRate: tax,
        };
      }
    }
  }
}
